import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";
import {useNavigate, useParams} from "react-router-dom";
import { useCreateJobMutation, useRescheduleJobMutation } from "../../../Store/Builder/schedule.api";
import { setLoader } from "../../../Store/Slice/techsbookSlice";
import showToast from "../../Hooks/toast";
import {clearState, setScheduleStep} from "../../../Store/Slice/scheduleSlice";
import {setActiveJob, setJustRescheduled} from "../../../Store/Slice/jobsSlice";

const useCreateJob = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {title: jobTitle} = useParams();
  const [createJob] = useCreateJobMutation();
  const [rescheduleJob] = useRescheduleJobMutation();
  const data = useSelector((state) => state.rootReducer.scheduleSlice.sendData);
  const radius = useSelector((state) => state.rootReducer.scheduleSlice.radius);
  const accountIndex = useSelector((state) => state.rootReducer.techsbookSlice.userData);

  const buildFormData = useCallback(() => {
    const body = new FormData();
    if (data?.purchase_order != null) {
      body.append("purchase_order", data.purchase_order);
    }
    body.append("job_datetime", data?.job_datetime);
    body.append("job_address", jobTitle ? (data?.location?.street || data?.job_address) : data?.job_address);
    body.append("technician_id", data?.technician_id);
    body.append("lat", data?.lat);
    body.append("lon", data?.lon);
    body.append("has_equipment", data?.has_equipment ? 1 : 0);

    if(accountIndex?.verification_key?.length || data?.verification_key){
      body.append("verification_key", accountIndex?.verification_key?.length ? accountIndex.verification_key : data?.verification_key);
    }

    if (data?.payment_method_id) {
      body.append("payment_method_id", data.payment_method_id);
    } else {
      body.append("stripe_payment_method", "affirm");
    }

      if (data?.credit_to_use) {
          body.append("credit_to_use", data.credit_to_use);
      }

    if(jobTitle){
      body.append("description", data.description);
    } else {
      body.append(
          "description",
          `${data?.description_more}${
              data?.verification_text ? "\n" + data.verification_text : ""
          }${data?.description ? "\n" + data.description : ""}`
      );
    }

    body.append("vehicle_count", data?.vehicle_count);

    if(data?.tracking_number) {
      body.append("tracking_number", data?.tracking_number);
    }

    if(data?.purchase_order) {
      body.append("purchase_order", data?.purchase_order);
    }

    if (!data?.has_equipment) {
      body.append("equipment_arrival_date", data.equipment_arrival_date);
    }

    if (data?.files) {
      Array.from(data.files).forEach((item, index) => {
        body.append(`files[${index}]`, item);
      });
    }

    if (data?.vehicle_files) {
      Array.from(data.vehicle_files).forEach((item, index) => {
        body.append(`vehicle_files[${index}]`, item);
      });
    }

    for (let key in data?.primary_contact) {
      body.append(`primary_contact[${key}]`, data.primary_contact[key]);
    }

    for (let key in data?.location) {
      body.append(`location[${key}]`, data.location[key]);
    }

    data?.job_items?.forEach((item, index) => {
      for (let key in item) {
        body.append(`job_items[${index}][${key}]`, item[key]);
      }
    });

    body.append("radius", radius);
    return body;
  }, [data, radius]);

  const create = useCallback(async () => {
    const body = buildFormData();
    dispatch(setLoader(true));

    try {
      const res = await createJob(body).unwrap();
      showToast(res?.message ?? "Success", "success");
      navigate(`/jobs/${res?.job_id}`);
      dispatch(clearState());
      dispatch(setActiveJob({}));
      dispatch(setScheduleStep(1));
    } catch (err) {
      showToast(err?.data?.message ?? "Error", "error");
      dispatch(clearState());
    } finally {
      dispatch(setLoader(false));
    }
  }, [buildFormData, createJob, dispatch, navigate]);

  const reschedule = useCallback(async (jobId) => {
        if (!jobId) {
          showToast("Invalid Job ID", "error");
          return false;
        }

        const body = buildFormData();
        dispatch(setLoader(true));

        try {
          const res = await rescheduleJob({ jobId, body }).unwrap();
          showToast(res?.message ?? "Job Rescheduled Successfully", "success");
          navigate(`/jobs/${res?.job_id}`);
          dispatch(setJustRescheduled(true));
        } catch (err) {
          navigate(`/jobs/${jobId}`);
          setTimeout(() => {
            showToast(err?.data?.message ?? "Failed to Reschedule", "error");
          }, 1000)
        } finally {
          dispatch(setLoader(false));
        }
      },
      [buildFormData, rescheduleJob, dispatch, navigate]
  );

  return { create, reschedule };
};

export default useCreateJob;
